import React, { useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  AlertDialogBody,
  AlertDialogFooter,
  Box,
  Button,
  ButtonText,
  Heading,
  HStack,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import type { ModuleUpgradeOffer } from '@/common/api/models/moduleUpgradeOffer';
import { BulletList } from '@/common/components/BulletList';
import FormField from '@/common/components/forms/FormField';
import { useAppToast } from '@/common/hooks/useAppToast';
import { useModuleUpgradeMutations } from '../hooks/useModuleUpgradeMutations';
import {
  buildUpgradeOfferDerivedCopy,
  formatModuleVersionLabel,
  gradeBasisEqual,
} from './moduleUpgradeOfferUtils';

interface ModuleUpgradeOfferModalProps {
  courseId: string;
  moduleId: string;
  upgradeOffer: ModuleUpgradeOffer;
  onClose: () => void;
  onSuccess?: () => void;
  onDismissed?: () => void;
  /** Lecturer preview: render student copy with inert actions. */
  previewMode?: boolean;
}

type UpgradeOfferFormValues = {
  acknowledgeTaskChanges: boolean;
  acknowledgeGradeChange: boolean;
  confirmText: string;
};

/**
 * ModuleUpgradeOfferModal
 * ----
 * Body and footer of the student upgrade dialog. The body only informs and
 * may scroll; every input that gates the update button sits in the footer,
 * so a disabled button always shows what it is waiting for.
 */
export const ModuleUpgradeOfferModal: React.FC<ModuleUpgradeOfferModalProps> = ({
  courseId,
  moduleId,
  upgradeOffer,
  onClose,
  onSuccess,
  onDismissed,
  previewMode = false,
}) => {
  const { t } = useTranslation();
  const showToastMessage = useAppToast();
  const { acceptMutation, dismissMutation } = useModuleUpgradeMutations();

  const removedTasks = upgradeOffer.removedTasks ?? [];
  const needsTypedConfirm = !!upgradeOffer.invalidatesProgress;
  const needsTaskChangeAcknowledge = removedTasks.length > 0;
  const needsGradeChangeAcknowledge = !!upgradeOffer.changeSummary
    && !gradeBasisEqual(
      upgradeOffer.changeSummary.gradeBasisBeforeByType,
      upgradeOffer.changeSummary.gradeBasisAfterByType,
    );
  const removedWithProgress = removedTasks.some((task) => task.hasProgress);

  const derivedCopy = useMemo(
    () => buildUpgradeOfferDerivedCopy(t, upgradeOffer),
    [t, upgradeOffer],
  );

  const acknowledgeTaskChangesLabel = t('student.module.upgrade-offer.acknowledge-task-changes');
  const acknowledgeGradeChangeLabel = t('student.module.upgrade-offer.acknowledge-grade-change');

  const formMethods = useForm<UpgradeOfferFormValues>({
    defaultValues: {
      acknowledgeTaskChanges: false,
      acknowledgeGradeChange: false,
      confirmText: '',
    },
  });

  const acknowledgeTaskChanges = useWatch({
    control: formMethods.control,
    name: 'acknowledgeTaskChanges',
  });
  const acknowledgeGradeChange = useWatch({
    control: formMethods.control,
    name: 'acknowledgeGradeChange',
  });
  const confirmText = useWatch({
    control: formMethods.control,
    name: 'confirmText',
  });

  const canSubmit = !previewMode
    && (!needsTypedConfirm || confirmText === 'UPGRADE')
    && (!needsTaskChangeAcknowledge || acknowledgeTaskChanges)
    && (!needsGradeChangeAcknowledge || acknowledgeGradeChange);

  const headline = upgradeOffer.headline?.trim();
  const impactNotice = upgradeOffer.impactNotice?.trim();
  const showLecturerNote = !!headline || !!impactNotice;
  const showTicks = needsGradeChangeAcknowledge || needsTaskChangeAcknowledge;

  const handleAccept = async (data: UpgradeOfferFormValues) => {
    try {
      await acceptMutation.mutateAsync({
        courseId,
        moduleId,
        data: {
          targetVersion: upgradeOffer.targetVersion,
          acknowledgeTaskChanges: data.acknowledgeTaskChanges,
          acknowledgeGradeChange: data.acknowledgeGradeChange,
        },
      });
      showToastMessage('success', t('student.module.upgrade-offer.success'));
      onSuccess?.();
      onClose();
    } catch {
      showToastMessage('error', t('student.module.upgrade-offer.error'));
    }
  };

  const handleDismiss = async () => {
    try {
      await dismissMutation.mutateAsync({
        courseId,
        moduleId,
        data: { targetVersion: upgradeOffer.targetVersion },
      });
      onDismissed?.();
      onClose();
    } catch {
      showToastMessage('error', t('student.module.upgrade-offer.dismiss-error'));
    }
  };

  const showDerivedSections = !derivedCopy.summaryUnavailable;

  return (
    <FormProvider {...formMethods}>
      <AlertDialogBody testID="module-upgrade-offer-modal">
        <VStack space="lg">
          {derivedCopy.summaryUnavailable ? (
            <Text size="sm" testID="module-upgrade-summary-unavailable">
              {t('student.module.upgrade-offer.summary-unavailable')}
            </Text>
          ) : null}

          {showDerivedSections && derivedCopy.whatYouGet.length > 0 ? (
            <VStack space="xs">
              <Heading size="sm" testID="module-upgrade-what-you-get-heading">
                {t('student.module.upgrade-offer.what-you-get-heading')}
              </Heading>
              <BulletList items={derivedCopy.whatYouGet} size="sm" />
            </VStack>
          ) : null}

          {showDerivedSections ? (
            <VStack space="xs">
              <Heading size="sm" testID="module-upgrade-how-graded-heading">
                {t('student.module.upgrade-offer.how-graded-heading')}
              </Heading>
              {derivedCopy.gradeBasisUnchanged ? (
                <Text size="sm" testID="module-upgrade-grade-same">
                  {t('student.module.upgrade-offer.grade-same-as-now', {
                    basis: derivedCopy.gradeBasisBeforeLine,
                  })}
                </Text>
              ) : (
                <VStack space="xs">
                  <HStack space="sm" alignItems="flex-start">
                    <Text size="sm" color="$textSubtle" minWidth={112}>
                      {t('student.module.upgrade-offer.grade-now-label')}
                    </Text>
                    <Text size="sm" flex={1} testID="module-upgrade-grade-now">
                      {derivedCopy.gradeBasisBeforeLine}
                    </Text>
                  </HStack>
                  <HStack space="sm" alignItems="flex-start">
                    <Text size="sm" color="$textSubtle" minWidth={112}>
                      {t('student.module.upgrade-offer.grade-after-label')}
                    </Text>
                    <Text
                      size="sm"
                      fontFamily="$heading"
                      flex={1}
                      testID="module-upgrade-grade-after"
                    >
                      {derivedCopy.gradeBasisAfterLine}
                    </Text>
                  </HStack>
                </VStack>
              )}
              {derivedCopy.showOralOnlyWarning ? (
                <Text size="sm" color="$textSubtle" testID="module-upgrade-oral-only-note">
                  {t('student.module.upgrade-offer.oral-exam-only-note')}
                </Text>
              ) : null}
            </VStack>
          ) : null}

          {needsTaskChangeAcknowledge ? (
            <VStack space="xs">
              <Heading size="sm" testID="module-upgrade-removed-tasks-heading">
                {t('student.module.upgrade-offer.removed-tasks-heading')}
              </Heading>
              <Text size="sm">
                {removedWithProgress
                  ? t('student.module.upgrade-offer.removed-tasks-with-progress-warning')
                  : t('student.module.upgrade-offer.removed-tasks-warning')}
              </Text>
            </VStack>
          ) : null}

          {showLecturerNote ? (
            <VStack space="xs">
              <Heading size="sm" testID="module-upgrade-lecturer-note-heading">
                {t('student.module.upgrade-offer.lecturer-note-heading')}
              </Heading>
              {headline ? (
                <Text size="sm" fontFamily="$heading" testID="module-upgrade-offer-headline">
                  {headline}
                </Text>
              ) : null}
              {impactNotice ? (
                <Text size="sm" testID="module-upgrade-offer-impact">
                  {impactNotice}
                </Text>
              ) : null}
            </VStack>
          ) : null}
        </VStack>
      </AlertDialogBody>

      <AlertDialogFooter
        testID="module-upgrade-offer-actions"
        borderTopWidth={1}
        borderTopColor="$borderLight"
        w="$full"
      >
        <VStack space="sm" w="$full" alignItems="stretch">
          {showTicks ? (
            <VStack
              space="xs"
              bg="$statusWarningSubtle"
              borderRadius="$lg"
              px="$3"
              py="$2"
              testID="module-upgrade-ticks"
            >
              {needsGradeChangeAcknowledge ? (
                <FormField
                  name="acknowledgeGradeChange"
                  type="checkbox"
                  label={acknowledgeGradeChangeLabel}
                  labelSize="sm"
                  rules={{
                    validate: (value: boolean) => value === true || acknowledgeGradeChangeLabel,
                  }}
                />
              ) : null}
              {needsTaskChangeAcknowledge ? (
                <FormField
                  name="acknowledgeTaskChanges"
                  type="checkbox"
                  label={acknowledgeTaskChangesLabel}
                  labelSize="sm"
                  rules={{
                    validate: (value: boolean) => value === true || acknowledgeTaskChangesLabel,
                  }}
                />
              ) : null}
            </VStack>
          ) : null}
          {needsTypedConfirm ? (
            <Box bg="$statusErrorSubtle" borderRadius="$lg" px="$3" py="$2">
              <Text size="sm" mb="$2">
                {t('student.module.upgrade-offer.progress-warning')}
              </Text>
              <FormField
                name="confirmText"
                type="text"
                label={t('student.module.upgrade-offer.type-confirm')}
                labelSize="sm"
                rules={{
                  validate: (value: string) => value === 'UPGRADE' || t('student.module.upgrade-offer.type-confirm'),
                }}
              />
            </Box>
          ) : null}
          <Button
            testID="module-upgrade-accept-btn"
            w="$full"
            onPress={previewMode ? undefined : formMethods.handleSubmit(handleAccept)}
            isDisabled={previewMode || !canSubmit || acceptMutation.isPending}
          >
            <ButtonText>
              {(upgradeOffer.changeSummary?.aiTutorGainedByType?.essay ?? 0) > 0
                ? t('student.module.upgrade-offer.accept-with-tutor', {
                  version: formatModuleVersionLabel(upgradeOffer.targetVersion),
                })
                : t('student.module.upgrade-offer.accept', {
                  version: formatModuleVersionLabel(upgradeOffer.targetVersion),
                })}
            </ButtonText>
          </Button>
          <HStack space="md" w="$full" justifyContent="center" alignItems="center">
            <Button
              testID="module-upgrade-remind-btn"
              variant="outline"
              action="secondary"
              onPress={previewMode ? undefined : onClose}
              isDisabled={previewMode || acceptMutation.isPending || dismissMutation.isPending}
            >
              <ButtonText>{t('student.module.upgrade-offer.remind-later')}</ButtonText>
            </Button>
            <Button
              testID="module-upgrade-dismiss-btn"
              variant="link"
              action="secondary"
              onPress={previewMode ? undefined : handleDismiss}
              isDisabled={previewMode || dismissMutation.isPending || acceptMutation.isPending}
            >
              <ButtonText>{t('student.module.upgrade-offer.hide-offer')}</ButtonText>
            </Button>
          </HStack>
          <Text
            size="xs"
            color="$textSubtle"
            textAlign="center"
            testID="module-upgrade-irreversible-warning"
          >
            {t('student.module.upgrade-offer.irreversible-warning')}
          </Text>
        </VStack>
      </AlertDialogFooter>
    </FormProvider>
  );
};

export default ModuleUpgradeOfferModal;
